import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { extendSession, logout } from "store/actions/session";
import { ApplicationState } from "store/state";
import {
    getTimeUntilLogoutMs,
    initializeIdleTimer,
    pauseActivityTracking,
    resetActivity,
    resumeActivityTracking,
    shouldLogout,
    shouldShowWarning,
} from "util/idle-timer";
import { IdleTimeoutModal } from "./idle-timeout-modal";

const CHECK_INTERVAL_MS = 10 * 1000;

const IdleTimeoutHandlerInner: React.FC = () => {
    const dispatch = useDispatch();

    const [showWarning, setShowWarning] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(0);

    const handleStayLoggedIn = useCallback(async () => {
        try {
            await extendSession();
            resetActivity();
            setShowWarning(false);
        } catch {
            // Failed to extend session
        }
    }, []);

    const handleLogout = useCallback(() => {
        setShowWarning(false);
        logout(dispatch);
    }, [dispatch]);

    const handleIdleLogout = useCallback(() => {
        setShowWarning(false);
        logout(dispatch, "idle");
    }, [dispatch]);

    const handleIdleLogoutRef = useRef(handleIdleLogout);
    handleIdleLogoutRef.current = handleIdleLogout;

    useEffect(() => {
        const cleanup = initializeIdleTimer();

        const checkIdleStatus = () => {
            const timeUntilLogout = getTimeUntilLogoutMs();
            const shouldWarn = shouldShowWarning();
            const shouldLogoutNow = shouldLogout();

            if (shouldLogoutNow) {
                handleIdleLogoutRef.current();
                return;
            }

            if (shouldWarn) {
                setShowWarning(true);
                setTimeRemaining(timeUntilLogout);
            } else {
                setShowWarning(false);
            }
        };

        const intervalId = window.setInterval(checkIdleStatus, CHECK_INTERVAL_MS);
        checkIdleStatus();

        return () => {
            cleanup();
            clearInterval(intervalId);
        };
    }, []);

    useEffect(() => {
        if (showWarning) {
            pauseActivityTracking();
        } else {
            resumeActivityTracking();
        }
    }, [showWarning]);

    useEffect(() => {
        if (!showWarning) return undefined;

        const interval = window.setInterval(() => {
            const remaining = getTimeUntilLogoutMs();
            setTimeRemaining(remaining);

            if (remaining <= 0) {
                handleIdleLogoutRef.current();
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [showWarning]);

    return (
        <IdleTimeoutModal
            isOpen={showWarning}
            timeRemainingMs={timeRemaining}
            onStayLoggedIn={handleStayLoggedIn}
            onLogout={handleLogout}
        />
    );
};

export const IdleTimeoutHandler: React.FC = () => {
    const baaEnforced = useSelector(
        (state: ApplicationState) => state.tenant.tenant?.baaEnforced ?? false,
    );

    if (!baaEnforced) {
        return null;
    }

    return <IdleTimeoutHandlerInner />;
};
